import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import i18n from '@/shared/lib/i18n';
import { apiAuthLogoutPost, notifyAuthChanged } from '@/entities/auth';
import { clearTokens, getRefreshToken } from '@/shared/lib/auth';
import { requestFlutterLogout } from '@/shared/lib/bridge';
import { disconnectRealtime } from '@/shared/lib/realtime';
import { showToast } from '@/shared/hooks/useToast';

export const useLogout = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const logout = useCallback(async () => {
    setIsLoading(true);
    const refresh = getRefreshToken();
    try {
      if (refresh) {
        await apiAuthLogoutPost({ refresh_token: refresh });
      }
    } catch {
      // even if server logout fails, clear local state
    } finally {
      // Tear down the previous user's WS first so no late-arriving frame
      // mutates the cache we're about to clear, then drop every cached
      // query so the next user doesn't see stale data from this one.
      disconnectRealtime();
      qc.clear();
      clearTokens();
      notifyAuthChanged();
      requestFlutterLogout();
      setIsLoading(false);
      navigate('/login', { replace: true });
      showToast({ kind: 'info', message: i18n.t('common:loggedOut') });
    }
  }, [navigate, qc]);

  return { logout, isLoading };
};
